import React, { useEffect } from 'react';
import SEO from './SEO';
import { Link } from 'react-router-dom';

const Opus3DDeleteAccount: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Delete Your Opus 3D Account | OmniView Labs"
                description="Request deletion of your Opus 3D account and associated personal data by contacting OmniviewLabs."
                canonical="/opus3d-delete-account"
            />

            {/* Header */}
            <div className="pt-32 pb-16 bg-[#F8FAFC] border-b border-slate-100 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-[0.2] pointer-events-none"></div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        Account Management
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-[#021226] mb-6 tracking-tight">Delete Your Opus 3D Account</h1>
                    <p className="text-lg text-slate-500 font-medium">Request removal of your account and personal data</p>
                </div>
            </div>

            <div className="container mx-auto px-6 py-20">
                <div className="max-w-2xl mx-auto space-y-12">

                    {/* How to Request */}
                    <section>
                        <h2 className="text-2xl md:text-3xl font-black text-[#021226] mb-6 tracking-tight flex items-center gap-4">
                            <span className="w-1.5 h-8 bg-[#FF5C00] rounded-full hidden md:block"></span>
                            How to Request Deletion
                        </h2>
                        <p className="text-slate-600 font-medium leading-relaxed mb-6">
                            To request deletion of your Opus 3D account and associated personal data, please email:
                        </p>
                        <a
                            href="mailto:info@omniviewlabs.com?subject=Delete%20My%20Opus%203D%20Account"
                            className="inline-flex items-center gap-3 px-6 py-4 bg-[#021226] text-white rounded-2xl font-black text-sm hover:bg-[#003B5C] transition-all active:scale-95 shadow-xl"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            info@omniviewlabs.com
                        </a>

                        <div className="mt-8 space-y-4">
                            <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">Subject</p>
                                <p className="text-[#021226] font-bold">Delete My Opus 3D Account</p>
                            </div>
                            <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Information to Include</p>
                                <ul className="space-y-2">
                                    <li className="flex items-center gap-2 text-slate-600 font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00] shrink-0"></span>
                                        Registered email address
                                    </li>
                                    <li className="flex items-center gap-2 text-slate-600 font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00] shrink-0"></span>
                                        Company name (if applicable)
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <p className="mt-6 text-sm text-slate-500 font-medium">
                            Deletion requests are processed within <span className="font-bold text-[#021226]">30 days</span>.
                        </p>
                    </section>

                    {/* What Gets Deleted vs Retained */}
                    <section>
                        <h2 className="text-2xl md:text-3xl font-black text-[#021226] mb-6 tracking-tight flex items-center gap-4">
                            <span className="w-1.5 h-8 bg-[#FF5C00] rounded-full hidden md:block"></span>
                            What Happens to Your Data
                        </h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-6 rounded-2xl bg-red-50 border border-red-100">
                                <div className="flex items-center gap-2 mb-4">
                                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                    <p className="text-xs font-black uppercase tracking-widest text-red-500">Data Deleted</p>
                                </div>
                                <ul className="space-y-2">
                                    {['User profile', 'Account information', 'Activity history', 'Uploaded media'].map(item => (
                                        <li key={item} className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                                            <svg className="w-4 h-4 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                                <div className="flex items-center gap-2 mb-4">
                                    <svg className="w-5 h-5 text-slate-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">Data Retained</p>
                                </div>
                                <ul className="space-y-2">
                                    {['Legal compliance records', 'Audit logs (up to 90 days)'].map(item => (
                                        <li key={item} className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                                            <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Footer */}
                    <div className="pt-12 border-t border-slate-100 text-center">
                        <p className="text-sm text-slate-400 mb-8 font-bold uppercase tracking-widest">
                            &copy; 2026 OmniviewLabs. All rights reserved.
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-[#021226] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#003B5C] transition-all active:scale-95 shadow-xl"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Opus3DDeleteAccount;
